import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Link,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StepIndicator from '../../components/auth/StepIndicator';
import PasswordStrength from '../../components/auth/PasswordStrength';
import PasswordField from '../../components/common/PasswordField';
import useForm from '../../hooks/useForm';
import { validators } from '../../utils/validators';
import { useAuthPageGuard } from '../../hooks/useAuthPageGuard';
import { register, clearError, getCurrentUser } from '../../redux/slices/authSlice';
import alertify from 'alertifyjs';

const RegisterPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { loading, error } = useSelector((state) => state.auth);

  // 🔐 Auth page guard - handles redirect (but not error clearing)
  // clearOnMount: false because this page uses inline Alert for errors
  useAuthPageGuard({ clearOnMount: false, showErrors: false });

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [userType, setUserType] = useState('');
  const steps = ['Hesap Tipi', 'Bilgiler', 'Tamamlandı'];

  // Get validation rules based on selected userType
  const getValidationRules = () => {
    const baseRules = {
      email: validators.email,
      password: validators.password,
      firstName: (val) => validators.required(val, 'Ad'),
      lastName: (val) => validators.required(val, 'Soyad')
    };

    if (userType === 'individual') {
      baseRules.tcKimlik = (val) => {
        // Optional - only validate if provided
        if (val && !/^\d{11}$/.test(val)) {
          return 'TC Kimlik No 11 haneli olmalıdır';
        }
        return null;
      };
    }

    if (userType === 'company_owner') {
      baseRules.companyName = (val) => validators.required(val, 'Şirket Unvanı');
      baseRules.taxNumber = (val) => {
        if (!val) return 'Vergi numarası gereklidir';
        if (!/^\d{10}$/.test(val)) return 'Vergi numarası 10 haneli olmalıdır';
        return null;
      };
    }

    return baseRules;
  };

  // Form hook - initialize with all possible fields
  const { formData, errors, touched, handleChange, handleBlur, setFormData, setErrors, setTouched } = useForm(
    {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      tcKimlik: '',
      companyName: '',
      taxNumber: ''
    },
    {}
  );

  // Clear error on unmount (cleanup)
  useEffect(() => {
    return () => {
      if (error) {
        dispatch(clearError());
      }
    };
  }, [dispatch, error]);

  // Handle userType selection (Step 1)
  const handleUserTypeSelect = (selectedUserType) => {
    setUserType(selectedUserType);
    setCurrentStep(2);
    // Reset form data when userType changes
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      tcKimlik: '',
      companyName: '',
      taxNumber: ''
    });
  };

  // Handle back button
  const handleBack = () => {
    setCurrentStep(1);
    setUserType('');
    dispatch(clearError());
  };

  // Handle form submission (Step 2)
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields with current userType rules
    const rules = getValidationRules();
    const validationErrors = {};
    
    Object.keys(rules).forEach((field) => {
      const error = rules[field](formData[field]);
      if (error) {
        validationErrors[field] = error;
      }
    });

    if (Object.keys(validationErrors).length > 0) {
      // Set errors to form state (to show red fields)
      setErrors(validationErrors);
      
      // Mark all validated fields as touched (to show errors)
      const newTouched = {};
      Object.keys(rules).forEach(field => {
        newTouched[field] = true;
      });
      setTouched(newTouched);
      
      // Show first error message to user
      const firstError = Object.values(validationErrors)[0];
      alertify.error(firstError);
      return;
    }

    try {
      // Prepare registration data
      const registrationData = {
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        userType: userType
      };

      // Add userType-specific fields
      if (userType === 'individual' && formData.tcKimlik && formData.tcKimlik.trim()) {
        registrationData.tcKimlik = formData.tcKimlik.trim();
      }

      if (userType === 'company_owner') {
        registrationData.companyName = formData.companyName.trim();
        registrationData.taxNumber = formData.taxNumber.trim();
      }

      // Dispatch register action
      const result = await dispatch(register(registrationData)).unwrap();

      if (result) {
        alertify.success('Kayıt başarılı! Dashboard\'a yönlendiriliyorsunuz...');
        setCurrentStep(3);

        // Phase 5: Fetch menu and permissions after registration
        // This ensures menu loads without requiring page refresh
        dispatch(getCurrentUser());

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          history.push('/dashboard');
        }, 2000);
      }
    } catch (err) {
      // Display error message from backend
      const errorMessage = err.message || err || 'Kayıt sırasında bir hata oluştu';
      alertify.error(errorMessage);
      console.error('Registration error:', err);
    }
  };

  // Render Step 1: UserType Selection
  const renderUserTypeSelection = () => (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 700 }}>
        Hesap Oluştur
      </Typography>
      <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
        Hesap tipinizi seçerek başlayın
      </Typography>

      <FormControl component="fieldset" fullWidth>
        <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
          Hesap Tipi Seçin
        </FormLabel>
        <RadioGroup value={userType} onChange={(e) => setUserType(e.target.value)}>
          {/* Individual User */}
          <Paper
            elevation={userType === 'individual' ? 3 : 1}
            sx={{
              p: 3,
              mb: 2,
              cursor: 'pointer',
              border: userType === 'individual' ? '2px solid' : '1px solid',
              borderColor: userType === 'individual' ? 'primary.main' : 'divider',
              transition: 'all 0.3s',
              '&:hover': { elevation: 3 }
            }}
            onClick={() => setUserType('individual')}
          >
            <Box display="flex" alignItems="center">
              <PersonIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box flex={1}>
                <FormControlLabel
                  value="individual"
                  control={<Radio />}
                  label={
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Bireysel Kullanıcı
                    </Typography>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Şahsi belgelerinizi oluşturmak için bireysel hesap açın
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Company Owner */}
          <Paper
            elevation={userType === 'company_owner' ? 3 : 1}
            sx={{
              p: 3,
              cursor: 'pointer',
              border: userType === 'company_owner' ? '2px solid' : '1px solid',
              borderColor: userType === 'company_owner' ? 'primary.main' : 'divider',
              transition: 'all 0.3s',
              '&:hover': { elevation: 3 }
            }}
            onClick={() => setUserType('company_owner')}
          >
            <Box display="flex" alignItems="center">
              <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box flex={1}>
                <FormControlLabel
                  value="company_owner"
                  control={<Radio />}
                  label={
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Şirket Sahibi
                    </Typography>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Şirketiniz için sözleşme ve belgeler oluşturun
                </Typography>
              </Box>
            </Box>
          </Paper>
        </RadioGroup>
      </FormControl>

      <Button
        variant="contained"
        size="large"
        fullWidth
        disabled={!userType}
        onClick={() => handleUserTypeSelect(userType)}
        sx={{ mt: 4, py: 1.5 }}
      >
        Devam Et
      </Button>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Zaten hesabınız var mı?{' '}
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push('/login')}
            sx={{ fontWeight: 600 }}
          >
            Giriş Yapın
          </Link>
        </Typography>
      </Box>
    </Box>
  );

  // Render Step 2: Registration Form
  const renderRegistrationForm = () => (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 700 }}>
        {userType === 'individual' ? 'Bireysel Hesap' : 'Şirket Hesabı'}
      </Typography>
      <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
        Bilgilerinizi girerek hesabınızı oluşturun
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(clearError())}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {/* Email */}
        <TextField
          fullWidth
          label="E-posta"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.email && Boolean(errors.email)}
          helperText={touched.email && errors.email}
          margin="normal"
          required
          autoComplete="email"
        />

        {/* Password */}
        <PasswordField
          label="Şifre"
          name="password"
          value={formData.password}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.password && Boolean(errors.password)}
          helperText={touched.password && errors.password}
          required
          autoComplete="new-password"
        />

        {/* Password Strength Indicator */}
        <PasswordStrength password={formData.password} />

        <Divider sx={{ my: 3 }} />

        {/* First Name */}
        <TextField
          fullWidth
          label="Ad"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.firstName && Boolean(errors.firstName)}
          helperText={touched.firstName && errors.firstName}
          margin="normal"
          required
          autoComplete="given-name"
        />

        {/* Last Name */}
        <TextField
          fullWidth
          label="Soyad"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.lastName && Boolean(errors.lastName)}
          helperText={touched.lastName && errors.lastName}
          margin="normal"
          required
          autoComplete="family-name"
        />

        {/* Individual User Fields */}
        {userType === 'individual' && (
          <TextField
            fullWidth
            label="TC Kimlik No (Opsiyonel)"
            name="tcKimlik"
            value={formData.tcKimlik || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.tcKimlik && Boolean(errors.tcKimlik)}
            helperText={touched.tcKimlik && errors.tcKimlik ? errors.tcKimlik : '11 haneli TC Kimlik numaranız'}
            margin="normal"
            inputProps={{ maxLength: 11 }}
          />
        )}

        {/* Company Owner Fields */}
        {userType === 'company_owner' && (
          <>
            <TextField
              fullWidth
              label="Şirket Unvanı"
              name="companyName"
              value={formData.companyName || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.companyName && Boolean(errors.companyName)}
              helperText={touched.companyName && errors.companyName}
              margin="normal"
              required
              autoComplete="organization"
            />

            <TextField
              fullWidth
              label="Vergi Numarası"
              name="taxNumber"
              value={formData.taxNumber || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.taxNumber && Boolean(errors.taxNumber)}
              helperText={touched.taxNumber && errors.taxNumber}
              margin="normal"
              required
              inputProps={{ maxLength: 10 }}
            />
          </>
        )}

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            size="large"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            disabled={loading}
            sx={{ flex: 1 }}
          >
            Geri
          </Button>

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ flex: 2, py: 1.5 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Hesap Oluştur'}
          </Button>
        </Box>
      </form>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Zaten hesabınız var mı?{' '}
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push('/login')}
            sx={{ fontWeight: 600 }}
          >
            Giriş Yapın
          </Link>
        </Typography>
      </Box>
    </Box>
  );

  // Render Step 3: Success Message
  const renderSuccess = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        Kayıt Başarılı!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Hesabınız başarıyla oluşturuldu. Dashboard'a yönlendiriliyorsunuz...
      </Typography>
      <CircularProgress />
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Step Indicator */}
          {currentStep < 3 && <StepIndicator currentStep={currentStep} steps={steps} />}

          {/* Render current step */}
          {currentStep === 1 && renderUserTypeSelection()}
          {currentStep === 2 && renderRegistrationForm()}
          {currentStep === 3 && renderSuccess()}
        </Paper>
      </Container>
    </Box>
  );
};

export default RegisterPage;

